import * as React from 'react';
import styled, { DefaultTheme, useTheme } from 'styled-components/macro';
import CreatableSelect from 'react-select/creatable';
import Select, {
  ControlProps,
  CSSObjectWithLabel,
  FormatOptionLabelMeta,
  GroupBase,
  MenuPlacement,
  MenuPosition,
  OptionProps,
} from 'react-select';
import { MdArrowDropDown } from 'react-icons/md';
import { FolderOption } from './folders/customOption';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import { useTranslation } from 'react-i18next';

export type Option = {
  value: string | number;
  label: string | number;
  isFolderShared?: boolean;
  __isNew__?: boolean;
  access?: boolean | string;
};

type Props = {
  value: any;
  defaultValue?: any;
  options: Array<Option> | any;
  placeholder?: string;
  isLoading?: boolean;
  name?: string;
  className?: string;
  dropdownHeight?: string | number;
  height?: number;
  width?: number;
  disabled?: boolean;
  readonly?: boolean;
  zIndexProp?: number;
  menuZIndexProp?: number;
  creatable?: boolean;
  error?: boolean;
  inputId?: any;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: MenuPosition;
  menuPlacement?: MenuPlacement;
  ariaLabel?: string;
  onMenuScrollToBottom?: () => void;
  onMenuClose?: () => void;
  onChange: (value: any, actionMeta?: any) => void;
  onInputChange?: (inputValue: any, actionMeta?: any) => void;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => string;
  extendStyles?: {
    container?: CSSObjectWithLabel;
    menu?: CSSObjectWithLabel;
    control?: CSSObjectWithLabel;
    menuList?: CSSObjectWithLabel;
    valueContainer?: CSSObjectWithLabel;
    input?: CSSObjectWithLabel;
    singleValue?: CSSObjectWithLabel;
    option?: CSSObjectWithLabel;
    menuPortal?: CSSObjectWithLabel;
  };
  showIndicator?: boolean;
  isClearable?: boolean;
  isMulti?: any /* used as any because creatable select not considering it as boolean */;
  onCreateOption?: (e?: any) => void;
  isSearchable?: boolean;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  menuShouldBlockScroll?: boolean;
  minMenuHeight?: number;
  closeMenuOnScroll?: boolean | ((event: Event) => boolean) | undefined;
  filterOption?:
    | ((option: Option, rawInput: string) => boolean)
    | null
    | undefined;
  formatOptionLabel?:
    | ((option: any, labelMeta: FormatOptionLabelMeta<any>) => React.ReactNode)
    | undefined;
  customComponents?: Partial<SelectComponents<any, any, any>> | undefined;
  onMenuOpen?: (() => void) | undefined;
  closeMenuOnSelect?: boolean | undefined;
  hideSelectedOptions?: boolean | undefined;
  translateLabels?: boolean;
};

type DropdownArrowProps = {
  menuIsOpen?: boolean;
};

export const DropdownArrowIcon = styled.span<DropdownArrowProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding-right: 12px;
  position: relative;
  svg {
    ${props => props.menuIsOpen && 'transform: rotate(180deg);'}
  }
`;

export const OptionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
`;

export const OptionIconWrapper = styled.div`
  text-align: center;
`;

export const getControlStyles = (
  props:
    | ControlProps<Option, false, GroupBase<Option>>
    | ControlProps<FolderOption, false, GroupBase<FolderOption>>,
  themes: DefaultTheme
) => {
  const { isDisabled, menuIsOpen, selectProps } = props;

  if (isDisabled) {
    return { borderColor: 'transparent' };
  }
  if ((selectProps as any).error) {
    return { borderColor: themes.colors.danger[80] };
  }
  if (menuIsOpen) {
    return { borderColor: themes.colors.secondary[100] };
  }
  return { borderColor: themes.colors.neutral[40] };
};

export const getOptionStyles = (
  props: OptionProps<Option, false>,
  themes: DefaultTheme
) => {
  const { isFocused, isSelected, isDisabled } = props;
  if (isDisabled) {
    return {
      color: themes.colors.neutral[40],
      backgroundColor: themes.colors.neutral[10],
      cursor: 'not-allowed',
    };
  }
  if (isFocused || isSelected) {
    return {
      color: themes.colors.primary[100],
      backgroundColor: themes.colors.secondary[5],
    };
  }

  return {
    color: themes.colors.neutral[100],
    backgroundColor: themes.colors.white[100],
  };
};

export const Dropdown = (props: Props) => {
  const { t } = useTranslation();
  const {
    ariaLabel = 'Aria label is missing',
    name,
    options,
    value,
    defaultValue = undefined,
    placeholder = 'No Folders',
    onChange,
    getOptionLabel,
    getOptionValue,
    menuPosition = 'absolute',
    menuPlacement = 'auto',
    menuPortalTarget,
    inputId,
    className,
    dropdownHeight = 'auto',
    height = 48,
    width,
    disabled = false,
    creatable = true,
    error = false,
    onMenuScrollToBottom,
    onMenuClose,
    extendStyles,
    readonly,
    showIndicator = true,
    isClearable = false,
    onInputChange /* SUS-1302 changes */,
    onCreateOption /* SUS-1302 changes */,
    isMulti = false /* SUS-1302 changes */,
    zIndexProp = 9999,
    menuZIndexProp = 2,
    isSearchable,
    formatCreateLabel,
    closeMenuOnScroll = undefined,
    menuShouldBlockScroll = false,
    isLoading = false,
    filterOption,
    formatOptionLabel,
    customComponents,
    onMenuOpen,
    closeMenuOnSelect,
    hideSelectedOptions,
    translateLabels = false,
  } = props;
  const themes = useTheme();

  //common props
  const commonProps = {
    error,
    options,
    className,
    name,
    placeholder,
    value,
    defaultValue,
    getOptionLabel,
    getOptionValue,
    onChange,
    menuPlacement,
    menuPosition,
    isDisabled: disabled,
    menuPortalTarget,
    'aria-label': ariaLabel,
    onMenuScrollToBottom,
    onMenuClose,
    isClearable,
    onInputChange,
    onCreateOption /* SUS-1302 changes */,
    isMulti /* SUS-1302 changes */,
    isSearchable,
    filterOption,
  };

  const DropdownIndicator = (props: {
    selectProps: { menuIsOpen: boolean | undefined };
  }) => {
    return showIndicator ? (
      <DropdownArrowIcon menuIsOpen={props.selectProps.menuIsOpen}>
        <MdArrowDropDown size={18} opacity={0.5} />
      </DropdownArrowIcon>
    ) : (
      <></>
    );
  };

  //dropdown styles
  const styles = {
    container: (base: CSSObjectWithLabel) => {
      return {
        ...base,
        ...(extendStyles?.container || {}),
      };
    },
    control: (
      base: CSSObjectWithLabel,
      props: ControlProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        width: width,
        boxShadow: 'none',
        borderRadius: 6,
        minHeight: '40px',
        pointerEvents: readonly ? 'none' : 'auto',
        ...getControlStyles(props, themes),
        ...(extendStyles?.control || {}),
        '&:hover': {
          borderColor: themes.colors.secondary[100],
        },
        '&:focus-within': {
          borderColor: themes.colors.secondary[100],
        },
      };
    },
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      width: '95%',
      ...(extendStyles?.singleValue || {}),
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      ...(extendStyles?.valueContainer || {}),
    }),
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      ...(extendStyles?.input || {}),
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: zIndexProp || 9999,
      ...(extendStyles?.menuPortal || {}),
    }),
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      ...(extendStyles?.menuList || {}),
      height: dropdownHeight,
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      marginTop: 2,
      zIndex: menuZIndexProp || 2,
      color: themes.colors.neutral[100],
      ...(extendStyles?.menu || {}),
    }),
    option: (
      base: CSSObjectWithLabel,
      props: OptionProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        height: height,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        fontWeight: 600,
        fontSize: 15,
        ...getOptionStyles(props, themes),
        ...(extendStyles?.option || {}),
      };
    },
  };

  if (creatable) {
    return (
      <CreatableSelect
        {...commonProps}
        inputId={inputId}
        isLoading={isLoading}
        components={{
          DropdownIndicator: DropdownIndicator,
          IndicatorSeparator: () => null,
          ...customComponents,
        }}
        styles={styles as any}
        isSearchable={isSearchable}
        formatCreateLabel={formatCreateLabel}
        formatOptionLabel={formatOptionLabel}
      />
    );
  }

  return (
    <Select
      {...commonProps}
      inputId={inputId}
      maxMenuHeight={220}
      menuShouldScrollIntoView={false}
      menuPortalTarget={menuPortalTarget}
      isLoading={isLoading}
      components={{
        DropdownIndicator: DropdownIndicator,
        IndicatorSeparator: () => null,
        ...customComponents,
      }}
      styles={styles as any}
      isSearchable={isSearchable}
      closeMenuOnScroll={closeMenuOnScroll}
      menuShouldBlockScroll={menuShouldBlockScroll}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      onMenuOpen={onMenuOpen}
      closeMenuOnSelect={closeMenuOnSelect}
      hideSelectedOptions={hideSelectedOptions}
      {...(translateLabels
        ? {
            getOptionLabel: (option: any) => t(option.label),
            getOptionValue: (option: any) => option.value,
          }
        : {})}
    />
  );
};
